import { useApi, configApiRef } from '@backstage/core-plugin-api';

/**
 * Configuration interface for sidebar access control
 * Each field contains an array of group entity references that have access to that sidebar item
 */
export interface SidebarAccessConfig {
  /** Home sidebar item access */
  home: string[];
  /** Catalog sidebar item access */
  catalog: string[];
  /** Groups sidebar item access */
  groups: string[];
  /** APIs sidebar item access */
  apis: string[];
  /** Docs sidebar item access */
  docs: string[];
  /** Create sidebar item access */
  create: string[];
  /** Tech Radar sidebar item access */
  techRadar: string[];
  /** Analytics sidebar item access */
  analytics: string[];
  /** Administration sidebar item access */
  administration: string[];
}

/**
 * Hook to get sidebar access configuration from app-config
 * Reads the app.sidebar.access configuration and provides default values
 * Configuration values are comma-separated strings of group entity references
 */
export const useSidebarAccessConfig = (): SidebarAccessConfig => {
  const configApi = useApi(configApiRef);

  const getAccessConfig = (key: string): string[] => {
    try {
      if (!configApi.has(`app.sidebar.access.${key}`)) {
        return [];
      }
      const configValue = configApi.getString(`app.sidebar.access.${key}`);
      if (!configValue || configValue.trim() === '') return [];
      // Split by comma and trim whitespace from each group reference
      return configValue
        .split(',')
        .map(item => item.trim())
        .filter(item => item !== '');
    } catch (error) {
      console.warn(`[RBAC] Failed to read config for ${key}:`, error);
      return [];
    }
  };

  return {
    home: getAccessConfig('home'),
    catalog: getAccessConfig('catalog'),
    groups: getAccessConfig('groups'),
    apis: getAccessConfig('apis'),
    docs: getAccessConfig('docs'),
    create: getAccessConfig('create'),
    techRadar: getAccessConfig('techRadar'),
    analytics: getAccessConfig('analytics'),
    administration: getAccessConfig('administration'),
  };
};
